import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTableColumns } from '@fortawesome/free-solid-svg-icons';
import { Dropdown, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAppContext } from 'providers/AppProvider';
import { RootState } from 'store'; // Adjust import path
import { startChat } from 'store/slices/trafficSlice';

import { useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';

// Locked, always-on columns the user can't hide.
const LOCKED_COLUMNS = ['country', 'actions', 'latest_active'];
const COLS_STORAGE_KEY = 'ecd_traffic_columns';

interface VisitorsTableProps {
  onRowClick: (visitor: VisitorData) => void;
  trafficInformation: any[];
  customerTypes: string;
  countryFilter?: string;
  search?: string;
}

const getCountryImageSRC = (country: any) => {
  if (country != undefined) {
    const country2 = country.toLowerCase();
    return 'https://cdn.easychatdesk.com/flags/' + country2 + '.svg';
  }

  return '';
};


const formatTimeAgo = (date: Date) => {
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000); // difference in seconds

  if (diff < 60) {
    return `${diff} second${diff !== 1 ? 's' : ''} ago`;
  } else if (diff < 3600) {
    const minutes = Math.floor(diff / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(diff / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
};


interface VisitorData {
  name: string;
  email: string;
  browser: string;
  country: string; // Assuming 'country' is a country code like 'IN'
  os: string;
  active: boolean;
  chat_started: boolean;
  customer_id: string;
  latest_active: Date;
}

const referrerLabel = (refer?: string) => {
  if (!refer) return 'Direct';
  try {
    return new URL(refer).hostname.replace(/^www\./, '');
  } catch {
    return refer.slice(0, 40);
  }
};

const fmtDuration = (ms: number) => {
  if (!isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return s + 's';
  const m = Math.floor(s / 60);
  if (m < 60) return m + 'm ' + (s % 60) + 's';
  const h = Math.floor(m / 60);
  return h + 'h ' + (m % 60) + 'm';
};

const columns: ColumnDef<VisitorData>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ getValue }) => {
      const name = getValue<string>();
      return (
        <p>{name ? <p className="pt-2">{name}</p> : <p>Unnamed Customer</p>}</p>
      );
    },
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ getValue }) => {
      const email = getValue<string>();
      return <p>{email ? <p className="pt-2">{email}</p> : <p>-</p>}</p>;
    },
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },

  {
    accessorKey: 'active',
    header: 'Status',
    cell: ({ getValue }) => {
      const isActive = getValue<boolean>();
      return (
        <Badge bg={isActive ? 'success' : 'secondary'} text="light">
          {isActive ? 'Active' : 'Inactive'}
        </Badge>
      );
    },
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },

  {
    accessorKey: 'refer',
    header: 'Source',
    cell: ({ row }) => (
      <p className="pt-2">{referrerLabel((row.original as any).refer)}</p>
    ),
    meta: { headerProps: { className: 'fs-10' }, cellProps: { className: 'py-2' } }
  },
  {
    id: 'time_on_site',
    header: 'Time on site',
    cell: ({ row }) => {
      const r = row.original as any;
      const start = r.first_seen ? new Date(r.first_seen).getTime() : NaN;
      if (!isFinite(start)) return <p className="pt-2 text-body-tertiary">—</p>;
      const end = r.active ? Date.now() : new Date(r.latest_active).getTime();
      return <p className="pt-2">{fmtDuration(end - start)}</p>;
    },
    meta: { headerProps: { className: 'fs-10' }, cellProps: { className: 'py-2' } }
  },
  {
    id: 'actions',
    header: 'Actions',
    enableHiding: false,
    cell: ({ row }) => {
      const { chat_started, active, customer_id } = row.original;

      const navigate = useNavigate();
      const dispatch = useDispatch();


        interface User {
    avatar_url?: string;
    // add other properties as needed
  }


      const token = useSelector((state: RootState) => state.auth.token);
      const license_uuid = useSelector((state: RootState) => state.auth.license_uuid);
      const currentUser = useSelector((state: RootState) => state.auth.user) as User | null;

      function generateUniqueId() {
        return `${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 9)}`;
      }

      const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation(); // Prevents the row click handler from being triggered

        if (!chat_started) {
          dispatch(
            startChat({
              customerId: customer_id,
              message: 'Hello, how are you',
              token: token ? token : '',
              ownerToken: license_uuid ? license_uuid : '',
              messageId: generateUniqueId(),
              avatar_url:  currentUser?.avatar_url ?? ""
            })
          );
        } else {
          navigate('/apps/chat/' + customer_id + '/conversation');
        }
      };

      return active == false ? (
        <p>Left website</p>
      ) : chat_started == true ? (
        <button className={`btn btn-outline-secondary`} onClick={handleClick}>
          Chat Started
        </button>
      ) : (
        <button
          className={`btn ${chat_started ? 'btn-secondary' : 'btn-primary'}`}
          onClick={handleClick}
        >
          Start Chat
        </button>
      );
    },
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },
  {
    accessorKey: 'browser',
    header: 'Browser',
    cell: ({ getValue }) => <p className="pt-2">{getValue<string>()}</p>,
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },
  {
    accessorKey: 'country',
    header: 'Country',
    enableHiding: false,
    cell: ({ getValue }) => {
      const countryCode = getValue<string>();
      return (
        <div className="d-flex align-items-center">
          <img src={getCountryImageSRC(countryCode)} width="28" />
          <span className="ms-2">{countryCode}</span>
        </div>
      );
    },
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },
  {
    accessorKey: 'os',
    header: 'OS',
    cell: ({ getValue }) => <p className="pt-2">{getValue<string>()}</p>,
    meta: {
      headerProps: {
        className: 'fs-10'
      },
      cellProps: { className: 'py-2' }
    }
  },
{
  accessorKey: 'latest_active',
  header: 'Latest Active',
  enableHiding: false,
  // Sort chronologically by parsed timestamp (values may be Date or string).
  sortingFn: (a, b, id) => {
    const av = new Date(a.getValue(id) as any).getTime() || 0;
    const bv = new Date(b.getValue(id) as any).getTime() || 0;
    return av - bv;
  },
  cell: ({ getValue }) => {
    const rawValue = getValue<string>();
    const date = rawValue ? new Date(rawValue) : null;
    return (
      <p className="pt-2">
        {date ? formatTimeAgo(date) : 'N/A'}
      </p>
    );
  },
  meta: {
    headerProps: {
      className: 'fs-10'
    },
    cellProps: { className: 'py-2' }
  }
}

];
export const VisitorsTable = ({
  onRowClick,
  trafficInformation,
  customerTypes,
  countryFilter,
  search
}: VisitorsTableProps) => {
    const filteredData = useMemo(() => {
      let data = trafficInformation;

      // Status filter
      if (customerTypes === 'chatting') data = data.filter(i => i.chat_started == true);
      else if (customerTypes === 'inactive') data = data.filter(i => i.active == false);
      else if (customerTypes === 'active') data = data.filter(i => i.active == true);

      // Country filter
      if (countryFilter && countryFilter !== 'all') {
        data = data.filter(i => (i.country || '').toLowerCase() === countryFilter.toLowerCase());
      }

      // Free-text search across name / email / page
      const q = (search || '').trim().toLowerCase();
      if (q) {
        data = data.filter(i =>
          [i.name, i.email, i.page, i.title, i.city]
            .filter(Boolean)
            .some((v: string) => String(v).toLowerCase().includes(q))
        );
      }

      return data;
    }, [customerTypes, trafficInformation, countryFilter, search]);



  // Remembered show/hide choices for the toggleable columns.
  const savedVisibility = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(COLS_STORAGE_KEY) || '{}') || {};
    } catch {
      return {};
    }
  }, []);

  const table = useAdvanceTable({
    data: filteredData,
    filter: customerTypes,
    columns,
    // Show every visitor in one view (no "View all" click needed). `pagination`
    // must be truthy or the hook ignores pageSize and falls back to 10; the high
    // cap then ensures the single page never truncates the traffic list.
    pagination: true,
    pageSize: Math.max(filteredData.length, 1000),
    sortable: true,
    // Always start ordered by most-recently-active first, and honor remembered
    // column visibility.
    initialState: {
      sorting: [{ id: 'latest_active', desc: true }],
      columnVisibility: savedVisibility
    }
  });

  // Persist column visibility whenever it changes.
  const visibility = table.getState().columnVisibility;
  useEffect(() => {
    try {
      localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(visibility));
    } catch {
      /* ignore */
    }
  }, [JSON.stringify(visibility)]); // eslint-disable-line react-hooks/exhaustive-deps

  const hideableColumns = table
    .getAllLeafColumns()
    .filter(col => col.getCanHide() && !LOCKED_COLUMNS.includes(col.id));

  return (
    <AdvanceTableProvider {...table}>
      <div className="d-flex justify-content-end mb-2">
        <Dropdown align="end" autoClose="outside">
          <Dropdown.Toggle variant="phoenix-secondary" size="sm">
            <FontAwesomeIcon icon={faTableColumns} className="me-2" />
            Columns
          </Dropdown.Toggle>
          <Dropdown.Menu className="p-2" style={{ minWidth: 200 }}>
            {hideableColumns.map(col => (
              <Form.Check
                key={col.id}
                type="checkbox"
                id={`col-toggle-${col.id}`}
                className="px-2 py-1 mb-0"
                label={String(col.columnDef.header ?? col.id)}
                checked={col.getIsVisible()}
                onChange={col.getToggleVisibilityHandler()}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
      <AdvanceTable
        tableProps={{
          size: 'sm',
          className: 'phoenix-table fs-8 mb-0 mt-3'
        }}
        headerClassName="fs-0"
        rowClassName="handcursor"
        onRowClick={row => onRowClick(row.original)}
      />
      <AdvanceTableFooter
        className="pt-3 pb-4"
        tableInfo="fs-8"
        showViewAllBtn={false}
      />
    </AdvanceTableProvider>
  );
};
